// dependencies
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ImageBackground, StyleSheet } from "react-native";
import { useIntl } from "react-intl";

// components
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import UIContainer from "@cloudspire/legacy-resources/src/components/UIContainer";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid";

// containers
import ContactInformation from "./Information";
import ContactForm from "./Form";

// contexts
import { useRoute } from "@cloudspire/legacy-resources/src/contexts/route";
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRequestContext } from "@cloudspire/legacy-resources/src/contexts/requestContext";

// constants
import {
  REQUEST_STATUS_VALUE_SUCCESS,
  REQUEST_STATUS_VALUE_IDLE,
  REQUEST_STATUS_VALUE_FETCHING,
  REQUEST_STATUS_VALUE_ERROR,
} from "@cloudspire/legacy-resources/src/constants/request";

// hooks
import { useResponse } from "@cloudspire/legacy-resources/src/hooks/fetcher";

// hocs
import useRecaptcha from "@cloudspire/legacy-resources/src/hocs/useRecaptcha";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";

// helpers
import roomGetCurrentRoomInChannel from "@cloudspire/legacy-resources/src/helpers/room/getCurrentRoomInChannel";
import { errorMonolithFormatErrors } from "@cloudspire/legacy-resources/src/helpers/error";
import { contactGetErrorMessage } from "@cloudspire/legacy-resources/src/helpers/contact";

// api
import { roomContactPage } from "@cloudspire/legacy-resources/src/api/room";
import { contactCreate } from "@cloudspire/legacy-resources/src/api/contact";

const styles = StyleSheet.create({
  imageBackground: {
    marginBottom: emToPx(-1),
  },
});

function ContactContainer() {
  const configuration = useConfiguration();

  const route = useRoute();

  const intl = useIntl();

  const query = route.getUri().getParsedQuery();

  const [parameterList, setParameterList] = useState({
    channelId: undefined,
    roomReference: query?.ref,
    checkin: query?.checkin,
    checkout: query?.checkout,
    capacity: query?.capacity,
    type: query?.type,
  });

  const { data: roomShow } = useResponse(
    roomContactPage({
      reference: query.ref,
      configuration,
    })
  );

  function handleChange(event) {
    if ("checkbox" === event.target.type) {
      setParameterList({
        ...parameterList,
        [event.target.name]: event.target.checked
          ? event.target.value
          : undefined,
      });
    } else if ("checkinCheckout" === event.target.name) {
      const newParameterList = { ...parameterList };

      const checkin = event.target.value.start
        ? event.target.value.start.format("Y-m-d")
        : "";

      const checkout = event.target.value.end
        ? event.target.value.end.format("Y-m-d")
        : "";

      newParameterList.checkin = checkin;
      newParameterList.checkout = checkout;

      setParameterList(newParameterList);
    } else {
      setParameterList({
        ...parameterList,
        [event.target.name]: event.target.value,
      });
    }
  }

  const hydrated = useRef(false);
  useEffect(
    function () {
      if (!hydrated.current) {
        if (configuration.channel.website.useBookingDelegation) {
          const channelId = roomGetCurrentRoomInChannel({
            room: roomShow?.data,
            configuration,
          })?.channelId;
          if (channelId) {
            setParameterList((parameterList) => ({
              ...parameterList,
              channelId,
            }));
          }
        }
        hydrated.current = true;
      }
    },
    [roomShow, configuration]
  );

  const requestContext = useRequestContext();

  const [status, setStatus] = useState(REQUEST_STATUS_VALUE_IDLE);
  const [error, setError] = useState(null);

  const generateToken = useRecaptcha({
    siteKey: configuration.channel.thirdPartyServices.google.recaptchaSiteKey,
    action: "contact",
  });

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      setError(null);
      setStatus(REQUEST_STATUS_VALUE_FETCHING);

      (async () => {
        const token = await generateToken().catch(() => {
          setStatus(REQUEST_STATUS_VALUE_ERROR);

          setError({
            message: intl.formatMessage({
              defaultMessage: "Impossible de savoir si vous êtes un humain.",
            }),
            list: {},
          });

          return null;
        });

        if (token) {
          try {
            await contactCreate({
              requestContext: {
                ...requestContext,
                channelId:
                  parameterList?.channelId ?? requestContext?.channelId,
              },
              parameterList: {
                ...parameterList,
                token,
              },
            });

            setStatus(REQUEST_STATUS_VALUE_SUCCESS);
          } catch (response) {
            setStatus(REQUEST_STATUS_VALUE_ERROR);

            setError({
              message: contactGetErrorMessage({
                message: response?.body?.message ?? "Une erreur est survenue",
                intl,
              }),
              list: errorMonolithFormatErrors({
                errorList: response?.body?.errors ?? {},
                intl,
              }),
            });
          }
        }
      })();
    },
    [generateToken, requestContext, parameterList, intl]
  );

  return (
    <>
      {configuration.channel.settings.website.contactFormCode ? (
        <div
          dangerouslySetInnerHTML={{
            __html: configuration.channel.settings.website.contactFormCode,
          }}
        />
      ) : (
        <ImageBackground
          source={{ uri: configuration.channel.website.backgroundUrl }}
          style={styles.imageBackground}
        >
          <Stack marginTop={3} />

          <UIContainer>
            <Row
              breakpointList={{ sm: 800, md: Infinity }}
              col={{ sm: 1, md: 3 }}
              gutter={0.5}
            >
              <Col span={{ sm: 1, md: 2 }}>
                <ContactForm
                  parameterList={parameterList}
                  status={status}
                  error={error}
                  onChange={handleChange}
                  onSubmit={handleSubmit}
                />
              </Col>

              <Col>
                <ContactInformation
                  delegatedChannelId={parameterList?.channelId}
                />
              </Col>
            </Row>
          </UIContainer>

          <Stack marginTop={3} />
        </ImageBackground>
      )}
    </>
  );
}

export default ContactContainer;
